import { Card, CardContent } from '@mui/material';
import {  useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'react-admin';
import { LayersControl, MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import '@themes/map-styles.css';
import { LatLngExpression, Layer, Map as LeafletMap } from 'leaflet';
import { GeoJSON } from 'react-leaflet'
import { FeatureCollection, Feature, Geometry } from 'geojson';
import { ZipCodeToDataMap, ZipDataDistribution, ZipDataDto } from '@models/zip-data';
import { LegendPanel } from '@components/map-legend-panel/map-legend-panel';
import { MapInfoPanelControl } from '@components/map-info-panel/map-info-panel-control';
import { useColor } from '@hooks/use-color';
import { ZipInfoPanel, ZipInfoPanelData } from '../ZipInfoPanel';
import { MapLocationMarker } from '@components/map-location-marker/map-location-marker';
import { MapDataLoadingPanel } from '@components/map-data-loading-panel/map-data-loading-panel';

// GeoJson
import texasZipCodes from '@assets/geojson/houston-texas-zip-codes.json';
import { useActiveRegion } from '@/hooks/use-active-region';
import { useMapOpacity } from '@/hooks/use-map-opacity';


export const ZipDataMap = ({ data, isLoading }: { data: ZipDataDistribution, isLoading?: boolean }) => {
  const [getColorByValue, colorMin, colorMax] = useColor();
  const [map, setMap] = useState<LeafletMap | null>(null);
  const [mapDataOpacity] = useMapOpacity();

  const [region, setRegion] = useActiveRegion();

  const [zipColorsMap, setZipColorsMap] = useState<Map<string, string>>(new Map<string, string>);
  const [initialCenter] = useState<LatLngExpression>([29.6827427, -95.4643671]);
  
  const [infoPanelData, setInfoPanelData] = useState<ZipInfoPanelData>();

  const [theme] = useTheme();

  // Navigation styles
  const defaultStyle = useMemo(()=> ({
    color: 'gray',
    weight: 2,
    opacity: mapDataOpacity,
    fillOpacity: mapDataOpacity
  }), [mapDataOpacity]);

  const hoverStyle = useMemo(() => ({
    color: colorMin,
    opacity: 1,
    fillOpacity: mapDataOpacity
  }), [mapDataOpacity, colorMin]);

  const selectedStyle = useMemo(() => ({
    color: colorMax,
    weight: 3,
    opacity: 1,
    fillOpacity: mapDataOpacity
  }), [mapDataOpacity, colorMax]);

  const zipDataMap = useMemo<ZipCodeToDataMap>(() => new Map<string, ZipDataDto>(data.zipData?.map(z => [z.zipCode, z])), [data]);

  useEffect(()=> {
    if (!data || data.zipData?.length === 0) return;
    texasZipCodes.features.forEach((feature: any) => {
      feature.properties.data = zipDataMap.get(feature.properties.Zip_Code);
    });

    if (region && region.type === 'zip') {
      const feature: any = texasZipCodes.features.find(f => f.properties.Zip_Code === region.id);
      const infoData = {
        ...feature.properties.data,
        label: feature.properties.Zip_Code
      };
      setInfoPanelData(infoData);
      setRegion({ ...region, data: feature.properties.data });
    }
  }, [data]);

  useEffect(() => {
    setZipColorsMap(new Map<string, string>(data.zipData?.map(z => [z.zipCode, getColorByValue(z.dataMean)])));
  }, [data, getColorByValue]);
  
  const getFeatureColorByCode = useCallback((code: string) => zipColorsMap.get(code), [data, zipColorsMap]);

  function style(feature: any) {
    let newStyle = defaultStyle;
    if (feature.properties.Zip_Code === infoPanelData?.zipCode) {
      newStyle = { ...defaultStyle, ...hoverStyle };
    }
    if (feature.properties.Zip_Code === region?.id) {
      newStyle = { ...defaultStyle, ...selectedStyle };
    }

    return {
      ...newStyle,
      fillColor: getFeatureColorByCode(feature.properties.Zip_Code)
    };
  }

  function onMouseOver(event: any) {
    event.target.bringToFront();    
    const data = {
      ...event.target.feature.properties.data,
      label: event.target.feature.properties.Zip_Code
    };
    setInfoPanelData(data);
  };

  function onMouseOut(event: any) {
    // const newStyle = event.target.feature.properties.Zip_Code === region.id ? selectedStyle : defaultStyle;
    // event.target.bringToBack();
    // event.target.setStyle(newStyle);
  };

  function onMouseClick(event: any) {
    const feature = event.target.feature;
    event.target.bringToFront();    

    setRegion({ 
      id: feature?.properties?.Zip_Code ?? '', 
      type: 'zip', 
      label: 'Zip ' + (feature?.properties?.Zip_Code ?? ''),
      data: feature?.properties?.data
    });
    //map?.fitBounds(event.target.getBounds());
  }

  function toolTipInfo(layer: any) {
    return layer.feature?.properties?.Zip_Code;
  };

  function onEachFeature(feature: Feature<Geometry, { Zip_Code: string }>, layer: Layer) {
    layer.bindTooltip(toolTipInfo, { sticky: true });

    layer.on({
      mouseover: onMouseOver,
      mouseout: onMouseOut,
      click: onMouseClick
    })
  }

  return (
    <Card>
      <CardContent>
        <MapContainer
          center={initialCenter}
          zoom={9}
          style={{ width: '100%', height: '75vh' }}
          ref={setMap}
          preferCanvas={true}
          key={theme} // use key to force re-render on theme change
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            className={ theme === 'dark' ? 'map-tiles' : ''}
          />

          <LegendPanel />
          <MapInfoPanelControl>
            <ZipInfoPanel data={infoPanelData} />
          </MapInfoPanelControl>
          <LayersControl position="bottomleft">
            <LayersControl.Overlay name="My Location">
              <MapLocationMarker />
            </LayersControl.Overlay>
            <LayersControl.Overlay name="Data" checked>
              {data.zipData && <GeoJSON
                onEachFeature={onEachFeature}
                data={texasZipCodes as FeatureCollection}
                style={style}
              />}
              {!data.zipData && <GeoJSON
                data={texasZipCodes as FeatureCollection}
                style={defaultStyle}
              />}
            </LayersControl.Overlay>
          </LayersControl>
        </MapContainer>
        <MapDataLoadingPanel isLoading={isLoading} />
      </CardContent>
    </Card>
  );
};
